import { FiPlus } from 'react-icons/fi';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import Header from 'src/component/header/header';
import './CreateTemplate.style.scss';
import Button from 'src/component/button/button';
import { useTemplateData } from 'src/Context/TemplateData.context';
import { STEP_META } from '../../../utils/TemplatesConst';
import SelectSubject from './SelectSubject';
import SelectTopic from './SelectTopic';
import SetQuestion from '../../../page/Exams/SetQuestion';
import SaveTemplateModal from './SaveTemplateModal';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getTemplateById } from 'src/api/template';
import { LoaderComponent } from 'src/utils/Helper';

const MAX_STEP = 3;
const MIN_STEP = 1;

const CreateTemplate = () => {
  const {
    selectedSubject,
    topicGroup,
    steps,
    setSteps,
    setIsSaveModalOpen,
    populateFormValue,
    setPageType,
    pageType,
    isGettingExtraInfo,
    setValidationError,
    validationError,
  } = useTemplateData();

  const { templateId: id } = useParams();
  const location = useLocation();

  const { title, para } = STEP_META[steps as keyof typeof STEP_META];

  const [loading, setLoading] = useState<boolean>(
    !!id && !location.pathname.includes('/templates/create')
  );

  const validateStep = (): boolean => {
    if (steps === 1 && selectedSubject.length === 0) {
      setValidationError('Please select at least one subject.');
      return false;
    }

    if (steps === 2) {
      const hasInvalidGroup =
        topicGroup.length === 0 ||
        topicGroup.some(
          group =>
            !group.name.trim() ||
            !Array.isArray(group.topics) ||
            group.topics.length === 0
        );

      if (hasInvalidGroup) {
        setValidationError(
          'Please ensure all topic groups have a name and at least one hierarchy path.'
        );
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;

    if (steps === MAX_STEP) {
      setIsSaveModalOpen(true);
      return;
    }

    setSteps(prev => (prev < MAX_STEP ? prev + 1 : MAX_STEP));
  };

  const goBack = () => {
    if (pageType === 'create') {
      setSteps(prev => (prev > MIN_STEP ? prev - 1 : MIN_STEP));
    }
  };

  const getContent = () => {
    switch (steps) {
      case 1:
        return <SelectSubject />;
      case 2:
        return <SelectTopic />;
      case 3:
        return <SetQuestion />;
      default:
        return <SelectSubject />;
    }
  };

  useEffect(() => {
    if (id && !location.pathname.includes('/templates/create')) {
      const newPageType = 'edit';
      setPageType(newPageType);
      setLoading(true);

      getTemplateById(id)
        .then(res => {
          populateFormValue(res.data);
        })
        .catch(err => {
          console.error('Error fetching template:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setPageType('create');
    }
  }, [id, location.pathname]);

  return (
    <div className="content create_template">
      <Header
        pageTitle={title}
        para={para}
        buttonText="Next"
        buttonIcon={<GoArrowRight />}
        isLeft={false}
        buttonStyle={
          steps === 1 && selectedSubject.length === 0
            ? {
                background: 'var(--gray-300)',
                borderColor: 'var(--gray-100)',
              }
            : {
                background: 'var(--gray-700)',
                borderColor: 'var(--gray-700)',
              }
        }
        onClick={goNext}
        extraButton={
          steps === MIN_STEP || pageType !== 'create' ? null : (
            <Button
              text="Back"
              icon={<GoArrowLeft />}
              style={{
                background: 'var(--gray-100)',
                color: 'var(--brand-red-700)',
              }}
              onClick={goBack}
            />
          )
        }
      />

      {validationError && (
        <p className="text-[red] text-[12px]">{validationError}</p>
      )}

      {loading || isGettingExtraInfo ? (
        <LoaderComponent label="Loading template…" />
      ) : (
        <div className="create_template_container">{getContent()}</div>
      )}

      <SaveTemplateModal />
    </div>
  );
};

export default CreateTemplate;
